/** @jsxImportSource theme-ui */
import { fetchDiseaseRelevanceAndDiseases, updateDiseaseRelevance } from 'admin/admin.saga';
import * as AdminSelectors from 'admin/admin.selectors';
import { Formik } from 'formik';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Grid } from 'semantic-ui-react';

import { hasIntersection, mapToDictionary } from 'utils/arrayHelpers';
import { nameof } from 'utils/typeHelpers';

import { FlexGroup } from 'components/_common/FlexGroup';
import SettingsSubmitButton from 'components/SidebarView/SettingsView/_common/SettingsSubmitButton';
import { MapDiseaseRelevanceMatrixFM2Payload } from 'components/SidebarView/SettingsView/CustomSettings/CustomSettingsHelpers';
import { DiseaseMatrixFM } from 'components/SidebarView/SettingsView/CustomSettings/CustomSettingsModels';
import { DiseaseRelevanceMatrixFormikControl } from 'components/SidebarView/SettingsView/CustomSettings/FormikControl.DiseaseRelevanceMatrix';
import { FormikSemanticDropDown } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticDropDown';

import diseasesSelectors from 'diseases/diseases.selectors';
import * as UserSelectors from 'user/user.selectors';

const RoleDiseaseRelevance: React.FC = () => {
  const dispatch = useDispatch();

  const userProfile = useSelector(UserSelectors.userProfileSelector);
  const rolesAndPresets = useSelector(UserSelectors.mapRolesWithPresets);
  const roleOptions = useSelector(UserSelectors.rolesAsDropdownItems);
  const diseases = useSelector(diseasesSelectors.allAvailableDiseasesById);
  const diseaseGroups = useSelector(AdminSelectors.customDiseaseRelevanceSelector);
  const isLoading = useSelector(AdminSelectors.isAdminLoadingSelector);

  const userRoleIdInitial = userProfile && userProfile.userType.id;

  function reloadDRMData() {
    dispatch(fetchDiseaseRelevanceAndDiseases());
  }

  useEffect(() => {
    reloadDRMData();
  }, []);

  const formikSeedValue = useMemo<DiseaseMatrixFM>(
    () => ({
      roleId: userRoleIdInitial,
      diseasesPerRole: {
        ...mapToDictionary(
          rolesAndPresets,
          x => x.id,
          x => ({ ...x.preset })
        ),
      },
    }),
    [userRoleIdInitial, rolesAndPresets]
  );

  const handleFormSubmit = useCallback((values: DiseaseMatrixFM) => {
    dispatch(
      updateDiseaseRelevance({
        id: values.roleId,
        data: MapDiseaseRelevanceMatrixFM2Payload(values.diseasesPerRole[values.roleId]),
      })
    );
  }, []);

  return (
    <Container>
      <h1>Role-Disease Relevance Configurations</h1>
      <Formik<DiseaseMatrixFM>
        enableReinitialize
        initialValues={formikSeedValue}
        onSubmit={handleFormSubmit}
      >
        {formikProps => {
          const { errors, touched, handleSubmit } = formikProps;
          const hasErrors = hasIntersection(Object.keys(errors), Object.keys(touched));
          return (
            <form onSubmit={handleSubmit}>
              <Grid columns={1}>
                <Grid.Row>
                  <Grid.Column>
                    <FlexGroup prefix="Role:">
                      <FormikSemanticDropDown
                        fluid
                        name={nameof<DiseaseMatrixFM>('roleId')}
                        placeholder="Select a role"
                        options={roleOptions}
                        error={touched.roleId && !!errors.roleId}
                      />
                    </FlexGroup>
                    <DiseaseRelevanceMatrixFormikControl
                      formikProps={formikProps}
                      diseaseGroupings={[{ groupName: 'By Category', subGroups: diseaseGroups }]}
                      diseases={diseases}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              <SettingsSubmitButton disabled={hasErrors || isLoading} text="Update Settings" />
            </form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default RoleDiseaseRelevance;
